import { globalComponents } from 'src/types/globalTypes';
import CertificatePage from '../CertificatePage';
import Captcha from 'src/components/ui-kit/Captcha';
import Input from 'src/components/ui-kit/Input';
import { useCallback, useEffect, useState } from 'react';
import PdfUpload from 'src/components/ui-kit/PdfUpload';
import ResultLoading from 'src/components/ui-kit/ResultLoading';
import {
  ContentWrapper,
  MainContent,
  SubContent,
  Item,
  ItemTitle,
  ItemContent,
  CaptchaDiv,
  ButtonDiv,
  Button,
  Annotation,
  ErrorMsg,
} from 'src/components/contents/certification/CertificatePage';
import { CertVerifyCode, IReqCertVerify, IResCertVerify } from 'src/types/api/certificateApiTypes';
import CertificateResult from '../CertificateResult';
import { BsFillPatchCheckFill } from 'react-icons/bs';
import { FaTimesCircle } from 'react-icons/fa';
import { certificateServiceProvider } from 'src/api/service/certificateService';
import { errorHandler } from 'src/api/errorHandler';
import { getCaptchaErrMsg } from 'src/utils/indexs';

const certificateService = certificateServiceProvider();

const initCertVerifyRes = {
  verifyResultCode: '',
  verifyResult: '',
};

/**
 * 電子證書驗證
 */
const CertVerify: React.FC = () => {
  const [pdfFile, setPdfFile] = useState<File>();
  // 是否有被拒絕的檔案(格式錯誤)
  const [isFileReject, setIsFileReject] = useState<boolean>(false);
  const [captchaUriSuffix, setCaptchaUriSuffix] = useState(Date.now());
  const [captchaUuid, setCaptchaUuid] = useState('');
  const [captcha, setCaptcha] = useState('');
  const [reqParams, setReqParams] = useState<IReqCertVerify>();

  // 頁面靜態資料
  const component = globalComponents.C;
  const colorType = component.color;
  const subtitle = '電子證書驗證';
  //欄位檢查
  const [isChecked, setIsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // 驗證碼檢查
  const [isCaptchaOk, setIsCaptchaOk] = useState<boolean>(false);
  const [captchaErrMsg, setCaptchaErrMsg] = useState<string>('請輸入驗證碼');
  // 回傳結果
  const [res, setRes] = useState<IResCertVerify>(initCertVerifyRes);

  // 插入請求資料
  useEffect(() => {
    pdfFile &&
      captcha &&
      setReqParams({
        file: pdfFile,
        captchaUuid: captchaUuid,
        captcha: captcha,
      });
  }, [pdfFile, captcha, captchaUuid]);

  useEffect(() => {
    const getReloadCaptcha = async () => {
      setCaptchaUriSuffix(Date.now());
      setCaptcha('');
      setRes(initCertVerifyRes);
    };
    pdfFile != null && getReloadCaptcha();
  }, [pdfFile]);

  const handleSubmit = useCallback(async () => {
    setIsChecked(true);
    const isCheckOk = () => {
      return reqParams?.captchaUuid && reqParams?.captcha && reqParams.file;
    };
    if (isCheckOk() && reqParams) {
      try {
        setIsSubmitting(true);
        const verfityRes = await certificateService.getCertVerify(reqParams);
        setRes(verfityRes);
        setIsChecked(false);
      } catch (e) {
        const errorMsg = errorHandler(e);
        setIsCaptchaOk(false);
        errorMsg && setCaptchaErrMsg(getCaptchaErrMsg(errorMsg));
        setRes(initCertVerifyRes);
      }
      setIsSubmitting(false);
    }
  }, [reqParams]);

  // 顯示結果
  const result = isSubmitting ?
    <CertificateResult isSubmitting={isSubmitting} isChecked={res.verifyResultCode === CertVerifyCode.V01}>
            證書驗證中<ResultLoading color={component.color.second} width={22} height={22} />
    </CertificateResult> : res.verifyResultCode && (
    <CertificateResult title="驗證結果" isSubmitting={isSubmitting} isChecked={res.verifyResultCode === CertVerifyCode.V01}>
      {res.verifyResultCode === CertVerifyCode.V01 ? (
        <>
          <span>
            <BsFillPatchCheckFill />
          </span>
          <span>{res.verifyResult}</span>
        </>
      ) : (
        <>
          <span>
            <FaTimesCircle />
          </span>
          <span>{res.verifyResult}</span>
        </>
      )}
    </CertificateResult>
  );

  // 處理鍵盤觸發（Enter 或 Space）
  const handleKeyDown = (e: React.KeyboardEvent) => {
  	if (e.key === 'Enter' || e.key === ' ') {
  		e.preventDefault(); // 防止 Space 鍵捲動頁面
		handleSubmit();
      }
  };
  
  return (
    <CertificatePage
      subtitle={subtitle}
      bannerBgImg={component.bgImgResultBanner}
      image={component.hoverImage}
      imgDot={component.bgImgDot}
      bgColor={colorType}
    >
      <ContentWrapper>
        <MainContent>
          <SubContent color={colorType.fifth}>
            <Item>
              <ItemTitle required>上傳電子證書PDF<em>(必填)</em></ItemTitle>
              <ItemContent>
                <PdfUpload
                  primaryColors={colorType}
                  setPdfFile={setPdfFile}
                  setIsFileReject={setIsFileReject}
                />
                {isFileReject && <ErrorMsg>請上傳PDF檔。</ErrorMsg>}
                {isChecked && !pdfFile && <ErrorMsg>請上傳電子證書 PDF。</ErrorMsg>}
              </ItemContent>
            </Item>
            <Item>
              <ItemTitle required><label htmlFor="input-captcha">驗證碼<em>(必填)</em></label></ItemTitle>
              <ItemContent>
                <CaptchaDiv>
                  <Input
                    value={captcha}
                    placeholder={'輸入驗證碼'}
                    onChange={setCaptcha}
                    width="100%"
                    primaryColors={colorType}
					inputId="input-captcha"
                  />
                  <Captcha
                    captchaUuid={captchaUuid}
                    setCaptchaUuid={setCaptchaUuid}
                    captchaUriSuffix={captchaUriSuffix}
                    setCaptchaUriSuffix={setCaptchaUriSuffix}
                    color={colorType.third}
                  />
                </CaptchaDiv>
                {isChecked && !isCaptchaOk && !isSubmitting &&<ErrorMsg>{captchaErrMsg}</ErrorMsg>}
              </ItemContent>
            </Item>
          </SubContent>
          <ButtonDiv>
            <Button
              onClick={() => {
                handleSubmit();
              }}
			  tabIndex={0} 
			  role="button" 
			  onKeyDown={handleKeyDown} 
			  aria-label="送出驗證"
            >
              送出驗證
            </Button>
          </ButtonDiv>
        </MainContent>
        {result}
        <Annotation>若有任何疑問，請洽客服專線：(02)8176-9009。</Annotation>
      </ContentWrapper>
    </CertificatePage>
  );
};

export default CertVerify;
